import { Vector2 } from '../../../odie';
import { gsap } from 'gsap';

import { arrayRandom } from '../../../lib/util/jsTools';
import { config } from '../../defs/config';
import {
  spawnBlockEntity,
  ThrowerBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import {
  blockIterateAll,
  cellHasBasicAndNoOverlay,
} from '../../util/blockTools';
import { mapIterateCells, mapToViewPosition } from '../../util/mapTools';
import { canSpawnBubbleOnCell } from '../../util/bubbleTools';
import { IEffect } from './IEffect';
import { createBlockView } from '../../../editor/support/blockViewFactory';

// types
//-----------------------------------------------------------------------------
// public
export type ThrowerEffectOptions = {
  subject: ThrowerBlockEntity;
};

/*
    spawns one new cage from the position of the thrower at a random (non thrower) block
*/
export class ThrowerEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _options: ThrowerEffectOptions;

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: ThrowerEffectOptions) {
    this._scene = scene;
    this._options = options;
  }

  public static assets(): string[] {
    return [];
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    const state = this._scene.sessionEntity.c.state;

    // require throws remaining
    if (state.cageThrowsRemaining === 0) return false;

    // pick a target
    const target = this._selectTarget();
    if (!target) return false;

    // Decrement throws remaining in session state
    state.cageThrowsRemaining -= 1;

    // spawn bubble at target position
    const cage = spawnBlockEntity(this._scene, { id: 'cage' }, target);

    // Set the cage to be invisible while animating
    cage.c.view2d.view.visible = false;

    // Get the target view position
    const to = mapToViewPosition(target);

    // animate cage throw
    await this._animateCageThrow(to);

    // Update blocks view state once when there are no more throws remaining
    if (state.cageThrowsRemaining <= 0) {
      this.updateThrowerBlocksEnabledState();
    }

    // Set the cage to be visible again
    cage.c.view2d.view.visible = true;

    return true;
  }

  /**
   * Update all thowers (soap bars) in the grid to reflect if they can still throw new cages (bubbles)
   */
  private updateThrowerBlocksEnabledState() {
    blockIterateAll(this._scene, (base) => {
      if (base.c.block.blockId === 'thrower') {
        base.c.blockThrower.updateEnabledState();
      }
    });
  }

  // private: animations
  //-------------------------------------------------------------------------
  private async _animateCageThrow(to: Vector2) {
    const duration = 0.5;
    const thrower = this._options.subject;
    const throwerView = thrower.c.view2d.view.getChildAt(0);
    const throwerPosition = mapToViewPosition(thrower.c.position.mapPosition);
    const offset = config.tile.size / 2;
    const toPosition = { x: to.x + offset, y: to.y + offset };
    const fromPosition = {
      x: throwerPosition.x + offset,
      y: throwerPosition.y + offset,
    };

    // Wobble the thrower for a better response that they are activated
    gsap
      .timeline()
      .to(throwerView, {
        scaleX: 0.8,
        scaleY: 1.2,
        duration: 0.1,
        ease: 'sine.out',
      })
      .to(throwerView, {
        scaleX: 1,
        scaleY: 1,
        duration: 1.1,
        ease: 'elastic.out',
      });

    // Create the temporary bubble view for animation
    const view = createBlockView({ id: 'cage' });
    view.anchorX = view.anchorY = 0.5;
    view.scaleX = view.scaleY = 0.5;
    this._scene.stage.addChild(view);

    // Animate the bubble spawning from the thrower
    gsap.to(view, {
      scaleX: 1,
      scaleY: 1,
      duration: duration * 0.5,
      ease: 'power1.out',
    });

    // animate to requested view pos
    const peak = Math.min(toPosition.y, fromPosition.y) - config.tile.size;
    view.x = fromPosition.x;
    view.y = fromPosition.y;

    // budget trajectory animation
    await gsap
      .timeline()
      .to(view, { y: peak, duration: duration * 0.5, ease: 'sine.out' })
      .to(view, { y: toPosition.y, duration: duration * 0.5, ease: 'sine.in' })
      .to(view, { x: toPosition.x, duration: duration, ease: 'none' }, 0);

    // Remove the temporary view
    this._scene.stage.removeChild(view);
  }

  // private: support
  //-------------------------------------------------------------------------
  /**
   * Select a valid target for a cage bubble.
   *
   * @returns A basic-block position, an empty fallback position, or undefined.
   */
  public _selectTarget(): Vector2 | undefined {
    // List of basic blocks without overlay that can be target for the cage (bubble)
    const candidatesBasic: Vector2[] = [];
    // List of empty cells that can be target for the cage (bubble), if the basic list is empty
    const candidatesEmpty: Vector2[] = [];

    // get target candidate positions
    mapIterateCells(this._scene, (cell, position) => {
      // Cell must support a bubble overlay.
      if (!canSpawnBubbleOnCell(cell)) return;

      // Prefer cells containing basic blocks.
      if (cellHasBasicAndNoOverlay(cell)) {
        candidatesBasic.push(position);
        return;
      }

      // Empty cells are fallback targets.
      if (!cell.base) candidatesEmpty.push(position);
    });

    // Prefer cells with basic blocks
    if (candidatesBasic.length) {
      // pick a random one
      return arrayRandom(candidatesBasic);
    }

    // Otherwise, pick an empty cell if available
    if (candidatesEmpty.length) {
      // pick a random one
      return arrayRandom(candidatesEmpty);
    }

    // No valid targets
    return undefined;
  }
}
